import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from './AppShell';
import { Select, SelectItem } from './Select';
import { API_BASE_URL } from '../config/api';
import { getAuthHeaders, clearAuth, getUser, patchUser } from '../utils/auth';
import type { ApiResponse } from '../types/api';
import './LanguageSettings.css';

// "all" 表示不过滤；其余为 ISO 639-1
type LearningLanguageValue = 'all' | 'en' | 'ja' | 'ko' | 'zh';

const LANGUAGE_OPTIONS: Array<{ value: LearningLanguageValue; label: string; desc: string }> = [
  { value: 'all', label: '全部语言', desc: '不过滤，首页展示所有语言的视频' },
  { value: 'ja',  label: '日语 (Japanese)',  desc: '首页只显示原文为日语的视频' },
  { value: 'en',  label: '英语 (English)',   desc: '首页只显示原文为英语的视频' },
  { value: 'ko',  label: '韩语 (Korean)',    desc: '首页只显示原文为韩语的视频' },
  { value: 'zh',  label: '中文 (Chinese)',   desc: '首页只显示原文为中文的视频' },
];

const LanguageSettings: React.FC = () => {
  const navigate = useNavigate();
  const [value, setValue] = useState<LearningLanguageValue>('all');
  const [initialValue, setInitialValue] = useState<LearningLanguageValue>('all');
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // 进入页面时从 /auth/me 拉一次最新值，避免 localStorage 中的快照过旧
  useEffect(() => {
    let aborted = false;
    const load = async () => {
      // 本地先填一个初值，避免 select 闪烁
      const local = getUser();
      if (local && typeof local.learningLanguage === 'string' && local.learningLanguage) {
        const v = (local.learningLanguage as LearningLanguageValue);
        const known = LANGUAGE_OPTIONS.some(o => o.value === v);
        if (known) {
          setValue(v);
          setInitialValue(v);
        }
      }
      try {
        const resp = await fetch(`${API_BASE_URL}/auth/me`, { headers: getAuthHeaders() });
        if (resp.status === 401) {
          clearAuth();
          navigate('/login');
          return;
        }
        const data: ApiResponse<{ learningLanguage?: string | null }> = await resp.json();
        if (aborted) return;
        if (data.success && data.data) {
          const raw = data.data.learningLanguage;
          const v: LearningLanguageValue = (raw && LANGUAGE_OPTIONS.some(o => o.value === raw))
            ? (raw as LearningLanguageValue)
            : 'all';
          setValue(v);
          setInitialValue(v);
          // 同步回 localStorage
          patchUser({ learningLanguage: raw ?? null });
        }
      } catch (e) {
        if (!aborted) setErrorMessage(e instanceof Error ? e.message : String(e));
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, [navigate]);

  const dirty = value !== initialValue;

  const handleSave = async () => {
    if (!dirty) return;
    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    try {
      const resp = await fetch(`${API_BASE_URL}/user/update-language-settings`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          learningLanguage: value === 'all' ? null : value,
        }),
      });
      if (resp.status === 401) {
        clearAuth();
        navigate('/login');
        return;
      }
      const data: ApiResponse<{ learningLanguage: string | null }> = await resp.json();
      if (!data.success) {
        setErrorMessage(data.error || '保存失败');
        return;
      }
      // 写回 localStorage，让首页立即生效
      patchUser({ learningLanguage: data.data?.learningLanguage ?? null });
      setInitialValue(value);
      setSuccessMessage('已保存。返回首页后将按所选语言展示视频。');
      setTimeout(() => setSuccessMessage(''), 4000);
    } catch (e) {
      setErrorMessage(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  const header = (
    <div className="language-settings-header app-shell-header-content">
      <button
        onClick={() => navigate('/profile/settings')}
        className="back-button"
        title="返回设置"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="19" y1="12" x2="5" y2="12"></line>
          <polyline points="12 19 5 12 12 5"></polyline>
        </svg>
      </button>
      <h1>语言设置</h1>
    </div>
  );

  return (
    <AppShell header={header} footer={null}>
      <div className="language-settings-container">
        <div className="language-settings-content">
          {loading ? (
            <div className="loading-message">加载中…</div>
          ) : (
            <>
              <div className="ls-section">
                <div className="ls-section-title">学习语言</div>
                <div className="ls-section-desc">
                  选择后，首页将只显示你选定语言的视频。可随时切换为「全部语言」取消过滤。
                </div>
                <div className="ls-select-wrap">
                  <Select
                    value={value}
                    onValueChange={(v) => setValue(v as LearningLanguageValue)}
                    disabled={saving}
                  >
                    {LANGUAGE_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </Select>
                </div>
                <div className="ls-current-desc">
                  {LANGUAGE_OPTIONS.find(o => o.value === value)?.desc}
                </div>
              </div>

              {errorMessage && <div className="error-message">{errorMessage}</div>}
              {successMessage && <div className="success-message">{successMessage}</div>}

              <div className="ls-actions">
                <button
                  type="button"
                  className="ls-save-button"
                  onClick={handleSave}
                  disabled={!dirty || saving}
                >
                  {saving ? '保存中…' : (dirty ? '保存' : '已保存')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </AppShell>
  );
};

export default LanguageSettings;
